<div class="ai-app">

  <!-- ═══════════════════════════════════════════════════ OVERVIEW ══ -->
  <ng-container *ngIf="currentView === 'overview'">

    <div class="ai-hero">
      <div class="ai-hero__glow"></div>
      <div class="ai-hero__content">
        <div class="ai-hero__badge">Adobe Illustrator</div>
        <h1 class="ai-hero__title">Master Vector Design</h1>
        <p class="ai-hero__tagline">From blank artboard to professional work — complete beginner to advanced.</p>

        <div class="ai-progress-bar" *ngIf="userId">
          <div class="ai-progress-bar__label">Core Curriculum Progress</div>
          <div class="ai-progress-bar__track">
            <div class="ai-progress-bar__fill" [style.width.%]="overallProgress"></div>
          </div>
          <div class="ai-progress-bar__pct">{{ overallProgress }}%</div>
        </div>
        <p class="ai-hero__auth-note" *ngIf="!userId">
          <a routerLink="/login">Sign in</a> to save your progress across sessions.
        </p>
      </div>
    </div>

    <!-- Level Legend -->
    <div class="ai-legend">
      <span class="ai-legend__item ai-legend__item--beginner">● Beginner</span>
      <span class="ai-legend__item ai-legend__item--intermediate">● Intermediate</span>
      <span class="ai-legend__item ai-legend__item--advanced">● Advanced</span>
    </div>

    <!-- Core Curriculum -->
    <div class="ai-section">
      <h2 class="ai-section__heading">Core Curriculum</h2>
      <p class="ai-section__sub">Complete these modules in order. Each builds on the last.</p>
      <div class="ai-grid">
        <div
          class="module-card"
          *ngFor="let mod of mainModules"
          [class.module-card--locked]="getModuleStatus(mod) === 'locked'"
          [class.module-card--visited]="getModuleStatus(mod) === 'visited'"
          [class.module-card--completed]="getModuleStatus(mod) === 'completed'"
          [class.module-card--beginner]="mod.level === 'beginner'"
          [class.module-card--intermediate]="mod.level === 'intermediate'"
          [class.module-card--advanced]="mod.level === 'advanced'"
          (click)="openModule(mod)"
        >
          <div class="module-card__number">{{ mod.number }}</div>
          <div class="module-card__icon">{{ mod.icon }}</div>
          <div class="module-card__body">
            <div class="module-card__level">{{ getLevelLabel(mod.level) }}</div>
            <h3 class="module-card__title">{{ mod.title }}</h3>
            <p class="module-card__subtitle">{{ mod.subtitle }}</p>
            <p class="module-card__time">⏱ {{ mod.estimatedTime }}</p>
          </div>
          <div class="module-card__status">
            <span *ngIf="getModuleStatus(mod) === 'locked'" class="badge badge--locked">🔒 Locked</span>
            <span *ngIf="getModuleStatus(mod) === 'available'" class="badge badge--available">Start →</span>
            <span *ngIf="getModuleStatus(mod) === 'visited'" class="badge badge--visited">In Progress</span>
            <span *ngIf="getModuleStatus(mod) === 'completed'" class="badge badge--completed">
              ✓ {{ getModuleProgress(mod.id)?.quizScore }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Specialty Tracks -->
    <div class="ai-section">
      <h2 class="ai-section__heading">Specialty Tracks</h2>
      <p class="ai-section__sub">Unlocks after completing Module 8. Choose your path — or explore both.</p>
      <div class="ai-grid ai-grid--branches">
        <div
          class="module-card module-card--branch"
          *ngFor="let mod of branchModules"
          [class.module-card--locked]="!isModuleUnlocked(mod)"
          [class.module-card--visited]="getModuleStatus(mod) === 'visited'"
          [class.module-card--completed]="getModuleStatus(mod) === 'completed'"
          (click)="openModule(mod)"
        >
          <div class="module-card__icon module-card__icon--lg">{{ mod.icon }}</div>
          <div class="module-card__body">
            <div class="module-card__level">{{ getLevelLabel(mod.level) }}</div>
            <h3 class="module-card__title">{{ mod.title }}</h3>
            <p class="module-card__subtitle">{{ mod.subtitle }}</p>
            <p class="module-card__time">⏱ {{ mod.estimatedTime }}</p>
          </div>
          <div class="module-card__status">
            <span *ngIf="!isModuleUnlocked(mod)" class="badge badge--locked">🔒 Complete Module 8 first</span>
            <span *ngIf="isModuleUnlocked(mod) && getModuleStatus(mod) === 'available'" class="badge badge--available">Explore →</span>
            <span *ngIf="getModuleStatus(mod) === 'visited'" class="badge badge--visited">In Progress</span>
            <span *ngIf="getModuleStatus(mod) === 'completed'" class="badge badge--completed">✓ Done</span>
          </div>
        </div>
      </div>
    </div>

  </ng-container>

  <!-- ══════════════════════════════════════ MODULE DETAIL ══ -->
  <ng-container *ngIf="currentView === 'module' && selectedModule">

    <div class="module-header" [attr.data-level]="selectedModule.level">
      <button class="btn-back" (click)="goToOverview()">← All Modules</button>
      <div class="module-header__meta">
        <span class="module-header__number">Module {{ selectedModule.number }}</span>
        <span class="module-header__level">{{ getLevelLabel(selectedModule.level) }}</span>
        <span class="module-header__tag" *ngIf="selectedModule.isBranchPath">Specialty Track</span>
      </div>
      <h2 class="module-header__title">{{ selectedModule.icon }} {{ selectedModule.title }}</h2>
      <p class="module-header__subtitle">{{ selectedModule.subtitle }}</p>
      <p class="module-header__time">⏱ {{ selectedModule.estimatedTime }}</p>
    </div>

    <!-- Lesson Sections -->
    <div class="lesson-body">
      <div class="lesson-section" *ngFor="let section of selectedModule.sections">
        <h3 class="lesson-section__title">{{ section.title }}</h3>
        <div class="lesson-section__content" [innerHTML]="section.content | forgeMarkdown"></div>

        <div class="callout" *ngIf="section.callout" [ngClass]="'callout--' + section.callout.type">
          <div class="callout__icon">
            <span *ngIf="section.callout.type === 'warning'">⚠️</span>
            <span *ngIf="section.callout.type === 'important'">❗</span>
            <span *ngIf="section.callout.type === 'tip'">💡</span>
            <span *ngIf="section.callout.type === 'fact'">📐</span>
          </div>
          <div class="callout__body">
            <strong class="callout__title">{{ section.callout.title }}</strong>
            <p class="callout__text">{{ section.callout.text }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Resources -->
    <div class="video-section" *ngIf="selectedModule.videos?.length">
      <h3 class="video-section__heading">📺 Recommended Videos</h3>
      <p class="video-section__sub">Curated for this module — search on YouTube or watch here.</p>
      <div class="video-grid">
        <div class="video-card" *ngFor="let video of selectedModule.videos">
          <div class="video-card__thumb" (click)="openVideo(video.youtubeId, video.title, video.searchQuery)">
            <img
              *ngIf="video.youtubeId"
              [src]="'https://img.youtube.com/vi/' + video.youtubeId + '/hqdefault.jpg'"
              [alt]="video.title"
              class="video-card__img"
            />
            <div *ngIf="!video.youtubeId" class="video-card__placeholder">
              <span>▶</span>
            </div>
            <div class="video-card__play">▶</div>
          </div>
          <div class="video-card__meta">
            <p class="video-card__title">{{ video.title }}</p>
            <p class="video-card__channel">{{ video.channel }}</p>
            <p class="video-card__desc">{{ video.description }}</p>
            <a
              class="video-card__link"
              [href]="getYouTubeUrl(video.searchQuery)"
              target="_blank"
              rel="noopener noreferrer"
            >Find on YouTube ↗</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Quiz CTA -->
    <div class="quiz-cta">
      <div class="quiz-cta__content">
        <div class="quiz-cta__icon">📝</div>
        <div class="quiz-cta__text">
          <h3>Comprehension Check</h3>
          <p *ngIf="!getModuleProgress(selectedModule.id)?.quizCompleted">
            {{ selectedModule.quiz.length }} questions to test your understanding. Encouraged, not required.
          </p>
          <p *ngIf="getModuleProgress(selectedModule.id)?.quizCompleted">
            Previous score: <strong>{{ getModuleProgress(selectedModule.id)?.quizScore }}%</strong>
            — Retake anytime to improve.
          </p>
        </div>
        <button class="btn-primary" (click)="startQuiz()">
          {{ getModuleProgress(selectedModule.id)?.quizCompleted ? 'Retake Quiz' : 'Take the Quiz' }}
        </button>
      </div>
    </div>

    <div class="module-nav">
      <button class="btn-back" (click)="goToOverview()">← Back to All Modules</button>
    </div>

  </ng-container>

  <!-- ════════════════════════════════════════════════ QUIZ ══ -->
  <ng-container *ngIf="currentView === 'quiz' && selectedModule">

    <div class="quiz-header">
      <button class="btn-back" (click)="backToModule()">← Back to Lesson</button>
      <h2 class="quiz-header__title">{{ selectedModule.icon }} {{ selectedModule.title }} — Quiz</h2>
    </div>

    <!-- Active Quiz -->
    <div class="quiz-body" *ngIf="!quizCompleted">
      <div class="quiz-progress">
        <span>Question {{ currentQuestionIndex + 1 }} of {{ selectedModule.quiz.length }}</span>
        <div class="quiz-progress__track">
          <div
            class="quiz-progress__fill"
            [style.width.%]="((currentQuestionIndex + (showExplanation ? 1 : 0)) / selectedModule.quiz.length) * 100"
          ></div>
        </div>
      </div>

      <div class="quiz-card" *ngIf="currentQuestion">
        <p class="quiz-card__type">{{ currentQuestion.type === 'true-false' ? 'True or False' : 'Multiple Choice' }}</p>
        <h3 class="quiz-card__question">{{ currentQuestion.question }}</h3>

        <div class="quiz-options">
          <button
            class="quiz-option"
            *ngFor="let option of currentQuestion.options; let i = index"
            [class.quiz-option--selected]="selectedAnswerIndex === i"
            [class.quiz-option--correct]="showExplanation && i === currentQuestion.correctIndex"
            [class.quiz-option--wrong]="showExplanation && selectedAnswerIndex === i && i !== currentQuestion.correctIndex"
            [disabled]="showExplanation"
            (click)="selectAnswer(i)"
          >
            <span class="quiz-option__letter">{{ ['A', 'B', 'C', 'D'][i] }}</span>
            <span class="quiz-option__text">{{ option }}</span>
          </button>
        </div>

        <div class="quiz-explanation" *ngIf="showExplanation">
          <div class="quiz-explanation__verdict"
            [class.quiz-explanation__verdict--correct]="isCurrentAnswerCorrect"
            [class.quiz-explanation__verdict--wrong]="!isCurrentAnswerCorrect">
            {{ isCurrentAnswerCorrect ? '✓ Correct!' : '✗ Not quite.' }}
          </div>
          <p class="quiz-explanation__text">{{ currentQuestion.explanation }}</p>
        </div>

        <div class="quiz-actions">
          <button
            class="btn-primary"
            *ngIf="!showExplanation"
            [disabled]="selectedAnswerIndex === null"
            (click)="submitAnswer()"
          >Submit Answer</button>
          <button
            class="btn-primary"
            *ngIf="showExplanation"
            (click)="nextQuestion()"
          >
            {{ currentQuestionIndex < selectedModule.quiz.length - 1 ? 'Next Question →' : 'Finish Quiz' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Quiz Results -->
    <div class="quiz-results" *ngIf="quizCompleted">
      <div class="quiz-results__score" [ngClass]="getScoreClass(quizScore)">
        <div class="quiz-results__pct">{{ quizScore }}%</div>
        <div class="quiz-results__label">
          {{ quizScore >= 70 ? 'Passed' : 'Keep Studying' }}
        </div>
      </div>

      <p class="quiz-results__quote">{{ getScoreMessage(quizScore) }}</p>

      <div class="quiz-results__breakdown">
        <h4>Question Breakdown</h4>
        <div
          class="quiz-results__item"
          *ngFor="let q of selectedModule.quiz; let i = index"
          [class.quiz-results__item--correct]="isAnswerCorrectForIndex(i)"
          [class.quiz-results__item--wrong]="!isAnswerCorrectForIndex(i)"
        >
          <span class="quiz-results__item-indicator">{{ isAnswerCorrectForIndex(i) ? '✓' : '✗' }}</span>
          <span class="quiz-results__item-q">{{ q.question }}</span>
        </div>
      </div>

      <div class="quiz-results__actions">
        <button class="btn-primary" (click)="startQuiz()">Retake Quiz</button>
        <button class="btn-secondary" (click)="backToModule()">← Back to Lesson</button>
        <button class="btn-secondary" (click)="goToOverview()">All Modules</button>
      </div>
    </div>

  </ng-container>

  <!-- ══════════════════════════════════════ VIDEO MODAL ══ -->
  <div class="video-modal" *ngIf="activeVideoId" (click)="closeVideo()">
    <div class="video-modal__inner" (click)="$event.stopPropagation()">
      <div class="video-modal__header">
        <span class="video-modal__title">{{ activeVideoTitle }}</span>
        <button class="video-modal__close" (click)="closeVideo()">✕</button>
      </div>
      <div class="video-modal__embed">
        <iframe
          [src]="'https://www.youtube.com/embed/' + activeVideoId + '?autoplay=1' | safeUrl"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  </div>

</div>
